if (!customElements.get('chic-location-slideshow')) {
  class ChicLocationSlideshow extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.slides = Array.from(this.querySelectorAll('.chic-locations__slide'));
        this.controls = this.querySelector('[data-location-controls]');
        this.toggle = this.querySelector('[data-location-toggle]');
        this.dots = Array.from(this.querySelectorAll('[data-location-index]'));
        this.status = this.querySelector('[data-location-status]');
        if (this.slides.length < 2 || !this.controls || !this.toggle) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.manualPaused = this.dataset.autoplay !== 'true' || this.motion.matches;
        this.index = 0;
        this.visible = true;
        this.interval = Math.max(3000, Number(this.dataset.interval) || 7000);
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.show(Number(dot.dataset.locationIndex), true), options);
        });
        this.toggle.addEventListener('click', () => {
          this.manualPaused = !this.manualPaused;
          this.updateToggle();
          this.schedule();
        }, options);
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (!event.target.matches('[data-location-index]')) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const index = (this.index + direction + this.slides.length) % this.slides.length;
          this.dots[index]?.focus();
          this.show(index, true);
        }, options);
        document.addEventListener('visibilitychange', () => this.schedule(), options);
        this.motion.addEventListener('change', () => {
          if (this.motion.matches) this.manualPaused = true;
          this.updateToggle();
          this.schedule();
        }, options);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            if (this.visible) this.prepareImages();
            this.schedule();
          });
          this.observer.observe(this);
        } else {
          this.prepareImages();
        }
        this.show(0);
        this.updateToggle();
        this.controls.hidden = false;
        this.classList.add('is-ready');
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    prepareImages() {
      this.querySelectorAll('.chic-locations__slide img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }

    updateToggle() {
      const label = this.manualPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.setAttribute('title', label);
      this.toggle.querySelector('[data-location-play]')?.toggleAttribute('hidden', !this.manualPaused);
      this.toggle.querySelector('[data-location-pause]')?.toggleAttribute('hidden', this.manualPaused);
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.manualPaused || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length), this.interval);
    }

    show(index, announce = false) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
      this.index = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot) => {
        const active = Number(dot.dataset.locationIndex) === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      if (announce && this.status) {
        this.status.textContent = this.status.dataset.template
          .replaceAll('[current]', String(index + 1))
          .replaceAll('[total]', String(this.slides.length));
      }
      this.schedule();
    }
  }

  customElements.define('chic-location-slideshow', ChicLocationSlideshow);
}
